// frontend/src/hooks/useConfirmDialog.js
import { useState, useCallback, useRef } from 'react';

/**
 * Hook that returns a Promise-based `confirm()` function and the props for
 * <ConfirmDialog>.
 *
 * A gate that also collects an answer — a text (`input: { label }`, a
 * cancellation reason), a pick (`choices: [{ value, label }]`) or a day
 * (`date: { label, initial, min }`) — resolves
 * with `{ value }` instead of `true`: an object, so an empty text is still a
 * confirmed answer, never a dismissal. `false` stays the one dismissal value
 * in every shape.
 *
 * `options` are OPAQUE here — whatever is passed to `confirm()` is spread into
 * `dialogProps` for the renderer to read.
 */
export function useConfirmDialog() {
  const [state, setState] = useState({ open: false, options: {} });
  const resolveRef = useRef(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ open: true, options });
    });
  }, []);

  // Re-created per gate (it closes over that gate's options) — the dialog
  // reads it fresh from dialogProps on every render, so nothing holds a stale one.
  const withValue = !!(state.options.input || state.options.choices || state.options.date);
  const handleConfirm = useCallback((value) => {
    setState({ open: false, options: {} });
    resolveRef.current?.(withValue ? { value } : true);
    resolveRef.current = null;
  }, [withValue]);

  const handleCancel = useCallback(() => {
    setState({ open: false, options: {} });
    resolveRef.current?.(false);
    resolveRef.current = null;
  }, []);

  return {
    confirm,
    dialogProps: {
      open: state.open,
      onConfirm: handleConfirm,
      onCancel: handleCancel,
      ...state.options,
    },
  };
}
